// pages/Dashboard.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-toastify';
import StatCard from '../components/ui/StatCard';
import { getIcon } from '../utils/icons';
import api from '../services/api';
import { getCategories } from '../services/categoryService';
import userService from '../services/userService';
import productService from '../services/productService';
import storeService from '../services/storeService';
import priceTagService from '../services/priceTagService';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

// Компонент для отображения статуса AI распознавания
const AIStatus = () => {
  const [aiEnabled, setAiEnabled] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAIStatus = async () => {
      try {
        setLoading(true);
        const response = await api.get('/system-settings/ai-enabled');
        setAiEnabled(response.data.value === 1);
      } catch (error) {
        console.error('Ошибка при получении статуса AI:', error);
        // Если endpoint не существует, показываем статус "неизвестно"
        setAiEnabled(null);
      } finally {
        setLoading(false);
      }
    };

    fetchAIStatus();
  }, []);

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
        <div className="flex items-center">
          <div className="animate-pulse w-4 h-4 bg-gray-300 rounded-full mr-3"></div>
          <span className="text-sm text-gray-600">Проверка статуса AI...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
      <div className="flex items-center">
        <div className={`w-3 h-3 rounded-full mr-3 ${
          aiEnabled === true ? 'bg-green-500' : 
          aiEnabled === false ? 'bg-red-500' : 'bg-gray-400'
        }`}></div>
        <span className="text-sm font-medium text-gray-700">
          Система AI распознавания:{' '}
          <span className={
            aiEnabled === true ? 'text-green-600' : 
            aiEnabled === false ? 'text-red-600' : 'text-gray-500'
          }>
            {aiEnabled === true ? 'Активна' : 
             aiEnabled === false ? 'Неактивна' : 'Неизвестно'}
          </span>
        </span>
      </div>
    </div>
  );
};

// Компонент для отображения последних действий
const RecentActivity = () => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchActivities = async () => {
      try {
        setLoading(true);
        
        // Поскольку в API нет прямого endpoint для audit-logs,
        // можем либо использовать заглушку, либо создать новый endpoint
        try {
          // Попытка вызвать API напрямую, если endpoint существует
          const response = await api.get('/audit-logs?limit=5');
          
          if (response.data && Array.isArray(response.data)) {
            // Преобразуем данные для отображения
            const formattedActivities = formatActivities(response.data);
            setActivities(formattedActivities);
          } else {
            throw new Error('Invalid response format');
          }
        } catch (apiError) {
          console.warn('API endpoint for audit logs not available:', apiError);
          
          // Заглушка: генерируем примерные данные для демонстрации
          const mockActivities = generateMockActivities();
          setActivities(mockActivities);
        }
      } catch (err) {
        console.error('Ошибка при получении активностей:', err);
        toast.error('Не удалось загрузить недавние действия');
        setActivities([]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchActivities();
  }, []);

// Функция форматирования активностей
const formatActivities = (data) => {
  return data.map(activity => {
    // Определяем тип значка на основе типа сущности
    const getActivityType = (entityType) => {
      const types = {
        'product': 'box',
        'price_tag': 'tag',
        'store': 'store',
        'user': 'user',
        'category': 'folder',
        'manufacturer': 'factory'
      };
      return entityType && types[entityType.toLowerCase()] || 'info';
    };

    // Форматируем время
    const formatTime = (timestamp) => {
      if (!timestamp) return 'недавно';
      
      const activityDate = new Date(timestamp);
      const now = new Date();
      const diffMs = now - activityDate;
      
      // Разница в минутах
      const diffMins = Math.round(diffMs / 60000);
      
      if (diffMins < 1) return 'только что';
      if (diffMins < 60) return `${diffMins} мин назад`;
      
      // Разница в часах
      const diffHours = Math.round(diffMins / 60);
      if (diffHours < 24) return `${diffHours} ч назад`;
      
      // Разница в днях
      const diffDays = Math.round(diffHours / 24);
      if (diffDays < 30) return `${diffDays} дн назад`;
      
      // Возвращаем полную дату
      return activityDate.toLocaleDateString();
    };

    // Формируем описание действия
    const getDescription = (action, entityType, details) => {
      // Преобразование действия в понятный текст
      const actionMap = {
        'create': 'Создание',
        'update': 'Обновление',
        'delete': 'Удаление',
        'verify': 'Проверка',
        'login': 'Вход в систему',
        'logout': 'Выход из системы',
        'refresh_token': 'Обновление сессии',
        'change_password': 'Изменение пароля',
        'register': 'Регистрация',
        'update_status': 'Изменение статуса',
        'sync': 'Синхронизация данных',
        'upload': 'Загрузка',
        'recognize': 'Распознавание'
      };
      
      // Преобразование типа сущности в понятный текст
      const entityMap = {
        'product': 'товара',
        'price_tag': 'ценника',
        'store': 'магазина',
        'user': 'пользователя',
        'category': 'категории',
        'manufacturer': 'производителя',
        'image': 'изображения',
      };
      
      const actionText = action && actionMap[action.toLowerCase()] || action || 'Действие';
      
      // Если нет типа сущности, но есть определенные действия, используем специальные описания
      if (!entityType) {
        if (action === 'login') return 'Вход в систему выполнен';
        if (action === 'logout') return 'Выход из системы выполнен';
        if (action === 'refresh_token') return 'Сессия обновлена';
        if (action === 'sync') return 'Синхронизация данных выполнена';
        return actionText;
      }
      
      const entityText = entityMap[entityType.toLowerCase()] || entityType;
      
      let desc = `${actionText} ${entityText}`;
      
      // Добавляем детали, если они есть
      if (details && typeof details === 'object') {
        if (details.name) {
          desc += ` "${details.name}"`;
        } else if (details.title) {
          desc += ` "${details.title}"`;
        } else if (details.fullName) {
          desc += ` "${details.fullName}"`;
        }
      }
      
      return desc;
    };
    
    return {
      id: activity.id || `activity-${Math.random().toString(36).substr(2, 9)}`,
      type: getActivityType(activity.entityType),
      action: activity.action,
      user: activity.user?.fullName || 'Система',
      time: formatTime(activity.createdAt),
      description: getDescription(activity.action, activity.entityType, activity.newValues)
    };
  });
};

  // Генерация тестовых данных (только для демо, если API endpoint недоступен)
  const generateMockActivities = () => {
    const activities = [
      {
        id: '1',
        type: 'box',
        user: 'Администратор',
        time: '5 мин назад',
        description: 'Создание товара "Молоко 3.5%"'
      },
      {
        id: '2',
        type: 'tag',
        user: 'Оператор',
        time: '25 мин назад',
        description: 'Добавление ценника "Хлеб белый"'
      },
      {
        id: '3',
        type: 'store',
        user: 'Менеджер',
        time: '1 ч назад',
        description: 'Обновление магазина "Супермаркет на Ленина"'
      },
      {
        id: '5',
        type: 'user',
        user: 'Администратор',
        time: '1 дн назад',
        description: 'Добавление пользователя "Иванов И.И."'
      }
    ];
    
    return activities;
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <h2 className="text-lg font-bold text-gray-800 mb-4">Последние действия</h2>
      
      {loading ? (
        <div className="flex items-center justify-center h-40">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      ) : activities.length === 0 ? (
        <p className="text-gray-500 text-center py-6">Нет недавних действий</p>
      ) : (
        <ul className="divide-y divide-gray-200">
          {activities.map((activity) => (
            <li key={activity.id} className="py-3">
              <div className="flex items-start">
                <span className="mt-1 mr-3 text-blue-500 flex-shrink-0">
                  {getIcon(activity.type)}
                </span>
                <div className="flex-grow">
                  <div className="flex justify-between">
                    <p className="text-sm font-medium text-gray-900">{activity.description}</p>
                    <p className="text-xs text-gray-500 whitespace-nowrap ml-2">{activity.time}</p>
                  </div>
                  <p className="text-xs text-gray-500">{activity.user}</p>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    
    </div>
  );
};

// Компонент для отображения быстрых действий
const QuickActions = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Определяем, какие действия показывать в зависимости от роли пользователя
  const getAvailableActions = () => {
    const allActions = [
      { id: 1, title: 'Добавить товар', icon: 'box', path: '/products/new', requiredRole: ['admin', 'manager'] },
      { id: 3, title: 'Добавить магазин', icon: 'store', path: '/stores', requiredRole: ['admin', 'manager'] },
      { id: 4, title: 'Загрузить ценник', icon: 'tag', path: '/price-tags', requiredRole: ['admin', 'manager', 'operator'] },
      { id: 5, title: 'Просмотр отчетов', icon: 'chart', path: '/reports', requiredRole: ['admin', 'analyst'] },
      { id: 6, title: 'Управление пользователями', icon: 'users', path: '/users', requiredRole: ['admin'] }
    ];
    
    // Если пользователь админ - показываем все действия
    if (user.role === 'admin') {
      return allActions;
    }
    
    // Иначе фильтруем по ролям
    return allActions.filter(action => 
      action.requiredRole.includes(user.role)
    );
  };
  
  const actions = getAvailableActions();

  const handleActionClick = (path) => {
    navigate(path);
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <h2 className="text-lg font-bold text-gray-800 mb-4">Быстрые действия</h2>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {actions.slice(0, 4).map((action) => (
          <button
            key={action.id}
            onClick={() => handleActionClick(action.path)}
            className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <span className="mr-3 text-blue-500">
              {getIcon(action.icon)}
            </span>
            <span className="text-gray-700">{action.title}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

// Компонент для отображения категорий
const CategoryStats = () => {
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCategoryStats = async () => {
      try {
        setLoading(true);
        
        try {
          // Пытаемся получить данные из reports/categories/analytics
          const response = await api.get('/reports/categories/analytics');
          if (response.data && Array.isArray(response.data)) {
            setStats(response.data);
          } else {
            throw new Error('Invalid response format');
          }
        } catch (apiError) {
          console.warn('API endpoint for category analytics not available:', apiError);
          
          // Альтернативный вариант - получаем категории и считаем товары
          const categories = await getCategories();
          
          // Если нет категорий, то выходим
          if (!categories || !Array.isArray(categories)) {
            setStats([]);
            return;
          }
          
          // Для демо можно добавить тестовые данные о количестве товаров
          const enrichedCategories = categories.map((category, index) => ({
            categoryId: category.id,
            categoryName: category.name,
            productCount: 10 - index > 0 ? 10 - index : 1, // Демо-данные
            priceTagCount: Math.floor(Math.random() * 20) // Демо-данные
          }));
          
          setStats(enrichedCategories);
        }
      } catch (err) {
        console.error('Ошибка при загрузке статистики категорий:', err);
        setStats([]);
      } finally {
        setLoading(false);
      }
    };

    fetchCategoryStats();
  }, []);

  const handleViewCategory = (categoryId) => {
    navigate(`/products?categoryId=${categoryId}`);
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-lg font-bold text-gray-800 mb-4">Категории товаров</h2>
        <div className="flex items-center justify-center h-40">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <h2 className="text-lg font-bold text-gray-800 mb-4">Категории товаров</h2>
      
      {stats.length === 0 ? (
        <p className="text-gray-500 text-center py-6">Нет данных по категориям</p>
      ) : (
        <div className="space-y-4">
          {stats.slice(0, 5).map((category) => (
            <div key={category.categoryId} className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900">{category.categoryName}</p>
                <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                  <div 
                    className="bg-blue-600 h-2 rounded-full" 
                    style={{ width: `${Math.min(100, (category.productCount / Math.max(...stats.map(s => s.productCount))) * 100)}%` }}
                  ></div>
                </div>
              </div>
              <div className="ml-4 text-right">
                <span className="text-sm font-semibold text-gray-700">{category.productCount}</span>
                <button
                  onClick={() => handleViewCategory(category.categoryId)}
                  className="block text-xs text-blue-600 hover:text-blue-800"
                >
                  Просмотр
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      
      <div className="mt-4 text-center">
        <button 
          onClick={() => navigate('/categories')}
          className="text-sm text-blue-600 hover:text-blue-800"
        >
          Все категории
        </button>
      </div>
    </div>
  );
};

// Компонент для отображения статистики по ценникам
const PriceTagStats = () => {
  const [stats, setStats] = useState({
    totalThisMonth: 0,
    totalLastMonth: 0,
    verifiedPercentage: 0,
    avgPricesByType: []
  });
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchPriceTagsStats = async () => {
      try {
        setLoading(true);
        
        // Задаем даты для статистики
        const today = new Date();
        const firstDayThisMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        const firstDayLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const lastDayLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
        
        try {
          // Пытаемся получить данные из специального endpoint для статистики (если есть)
          const response = await api.get('/reports/price-tags/summary');
          if (response.data) {
            setStats(response.data);
            return;
          }
        } catch (apiError) {
          console.warn('API endpoint for price tags statistics not available:', apiError);
          
          // Если нет специального endpoint, получаем данные из общего endpoint
          try {
            // Ценники за текущий месяц
            const thisMonthResponse = await api.get(`/price-tags?startDate=${firstDayThisMonth.toISOString()}&pageSize=1000`);
            const thisMonthTags = Array.isArray(thisMonthResponse.data) ? thisMonthResponse.data : [];
            
            // Ценники за прошлый месяц
            const lastMonthResponse = await api.get(`/price-tags?startDate=${firstDayLastMonth.toISOString()}&endDate=${lastDayLastMonth.toISOString()}&pageSize=1000`);
            const lastMonthTags = Array.isArray(lastMonthResponse.data) ? lastMonthResponse.data : [];
            
            // Считаем статистику
            const totalThisMonth = thisMonthTags.length;
            const totalLastMonth = lastMonthTags.length;
            const verifiedCount = thisMonthTags.filter(tag => tag.isVerified).length;
            const verifiedPercentage = totalThisMonth > 0 ? Math.round((verifiedCount / totalThisMonth) * 100) : 0;
            
            // Считаем среднюю цену по типам ценников
            const tagTypes = ['Printed', 'Handwritten', 'Electronic'];
            const avgPricesByType = tagTypes.map(type => {
              const typeTags = thisMonthTags.filter(tag => tag.tagType === type);
              const avgPrice = typeTags.length > 0
                ? Math.round(typeTags.reduce((sum, tag) => sum + tag.price, 0) / typeTags.length * 100) / 100
                : 0;
              
              return {
                type,
                count: typeTags.length,
                avgPrice
              };
            });
            
            setStats({
              totalThisMonth,
              totalLastMonth,
              verifiedPercentage,
              avgPricesByType
            });
          } catch (error) {
            console.error('Ошибка при получении данных о ценниках:', error);
            
            // Генерируем тестовые данные
            setStats({
              totalThisMonth: 156,
              totalLastMonth: 132,
              verifiedPercentage: 78,
              avgPricesByType: [
                { type: 'Printed', count: 92, avgPrice: 125.40 },
                { type: 'Handwritten', count: 43, avgPrice: 92.75 },
                { type: 'Electronic', count: 21, avgPrice: 187.30 }
              ]
            });
          }
        }
      } catch (err) {
        console.error('Ошибка при загрузке статистики ценников:', err);
        
        // Генерируем тестовые данные
        setStats({
          totalThisMonth: 156,
          totalLastMonth: 132,
          verifiedPercentage: 78,
          avgPricesByType: [
            { type: 'Printed', count: 92, avgPrice: 125.40 },
            { type: 'Handwritten', count: 43, avgPrice: 92.75 },
            { type: 'Electronic', count: 21, avgPrice: 187.30 }
          ]
        });
      } finally {
        setLoading(false);
      }
    };

    fetchPriceTagsStats();
  }, []);

  const getTagTypeName = (type) => {
    const types = {
      'Printed': 'Печатные',
      'Handwritten': 'Рукописные',
      'Electronic': 'Электронные'
    };
    return types[type] || type;
  };

  const getPercentChange = () => {
    if (stats.totalLastMonth === 0) return 100;
    const change = ((stats.totalThisMonth - stats.totalLastMonth) / stats.totalLastMonth) * 100;
    return Math.round(change);
  };

  const percentChange = getPercentChange();

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-lg font-bold text-gray-800 mb-4">Статистика ценников</h2>
        <div className="flex items-center justify-center h-40">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <h2 className="text-lg font-bold text-gray-800 mb-4">Статистика ценников</h2>
      
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="bg-blue-50 p-4 rounded-lg">
          <p className="text-sm text-gray-600">Ценники этот месяц</p>
          <p className="text-xl font-bold text-gray-800">{stats.totalThisMonth}</p>
          <div className={`flex items-center mt-1 text-xs ${percentChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            <span>{percentChange >= 0 ? '↑' : '↓'} {Math.abs(percentChange)}%</span>
            <span className="ml-1 text-gray-500">по сравнению с прошлым</span>
          </div>
        </div>
        <div className="bg-green-50 p-4 rounded-lg">
          <p className="text-sm text-gray-600">Проверено</p>
          <p className="text-xl font-bold text-gray-800">{stats.verifiedPercentage}%</p>
          <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
            <div 
              className="bg-green-500 h-2 rounded-full" 
              style={{ width: `${stats.verifiedPercentage}%` }}
            ></div>
          </div>
        </div>
      </div>
      
      <h3 className="font-medium text-gray-700 mb-3">Статистика по типам ценников</h3>
      
      {stats.avgPricesByType.length === 0 ? (
        <p className="text-gray-500 text-center py-4">Нет данных по типам ценников</p>
      ) : (
        <div className="space-y-4">
          {stats.avgPricesByType.map((item) => (
            <div key={item.type} className="flex justify-between items-center">
              <div className="flex items-center">
                <span className="w-3 h-3 rounded-full mr-2" 
                  style={{ 
                    backgroundColor: item.type === 'Printed' ? '#3B82F6' : 
                                    item.type === 'Handwritten' ? '#F59E0B' : '#10B981' 
                  }} 
                />
                <span className="text-sm font-medium">{getTagTypeName(item.type)}</span>
              </div>
              <div className="text-right">
                <p className="text-sm font-semibold">{item.count} шт.</p>
                <p className="text-xs text-gray-500">Средняя цена: {item.avgPrice} ₽</p>
              </div>
            </div>
          ))}
        </div>
      )}
      
      <div className="mt-4 text-center">
        <button 
          onClick={() => navigate('/price-tags')}
          className="text-sm text-blue-600 hover:text-blue-800"
        >
          Все ценники
        </button>
      </div>
    </div>
  );
};

// Компонент графика активности ценников
const PriceTagChart = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('week'); // week, month, year

  const getPeriodDates = (p) => {
    const end = new Date();
    const start = new Date();
    if (p === 'week') start.setDate(end.getDate() - 7);
    else if (p === 'month') start.setDate(end.getDate() - 30);
    else start.setFullYear(end.getFullYear() - 1);
    return { start, end };
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const { start, end } = getPeriodDates(period);
        const response = await api.get(`/reports/price-tags/daily-stats?startDate=${start.toISOString()}&endDate=${end.toISOString()}`);

        const raw = response.data || [];
        // Форматируем даты для отображения
        const formatted = raw.map(item => ({
          date: new Date(item.date).toLocaleDateString('ru-RU', {
            day: 'numeric',
            month: period === 'year' ? 'short' : 'numeric'
          }),
          count: item.count
        }));
        setData(formatted);
      } catch (err) {
        console.error('Ошибка загрузки графика:', err);
        setData([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [period]);

  const periods = [
    { key: 'week', label: '7 дней' },
    { key: 'month', label: '30 дней' },
    { key: 'year', label: 'Год' }
  ];

  return (
    <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold text-gray-800">Активность по ценникам</h2>
        <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
          {periods.map(p => (
            <button
              key={p.key}
              onClick={() => setPeriod(p.key)}
              className={`px-3 py-1 text-sm rounded-md transition-colors ${
                period === p.key
                  ? 'bg-blue-500 text-white'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      ) : data.length === 0 ? (
        <div className="flex items-center justify-center h-64 text-gray-400">
          Нет данных за выбранный период
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={280}>
          <AreaChart data={data} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
            <defs>
              <linearGradient id="colorCount" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3}/>
                <stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis
              dataKey="date"
              tick={{ fontSize: 12, fill: '#9CA3AF' }}
              tickLine={false}
              axisLine={{ stroke: '#E5E7EB' }}
            />
            <YAxis
              tick={{ fontSize: 12, fill: '#9CA3AF' }}
              tickLine={false}
              axisLine={false}
              allowDecimals={false}
            />
            <Tooltip
              contentStyle={{
                borderRadius: '8px',
                border: '1px solid #E5E7EB',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
              }}
              formatter={(value) => [`${value} шт.`, 'Ценников']}
            />
            <Area
              type="monotone"
              dataKey="count"
              stroke="#3B82F6"
              strokeWidth={2}
              fill="url(#colorCount)"
            />
          </AreaChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};

/**
 * Страница панели управления (дашборд)
 * @returns {JSX.Element} - Компонент для рендеринга
 */
const DashboardPage = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    products: 0,
    stores: 0,
    priceTags: 0,
    users: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Проверяем наличие endpoint для статистики дашборда
        try {
          const dashboardStatsResponse = await api.get('/reports/dashboard-stats');
          if (dashboardStatsResponse.data) {
            setStats(dashboardStatsResponse.data);
            return;
          }
        } catch (dashboardError) {
          console.warn('Dashboard stats endpoint not available, trying separate requests:', dashboardError);
        }
        
        // Если нет специального endpoint, используем отдельные запросы
        try {
          // Запрашиваем с pageSize=1 для минимизации трафика, нам нужен только totalCount
          const [productsResponse, storesResponse, priceTagsResponse] = await Promise.all([
            productService.getProducts({page: 1, pageSize: 1}),
            storeService.getStores({page: 1, pageSize: 1}),
            priceTagService.getPriceTags({page: 1, pageSize: 1})
          ]);
          
          // Для корректного отображения статистики
          // Проверяем, есть ли в ответах дополнительная информация о totalCount
          const getCount = (response) => {
            // Если ответ - массив с данными
            if (Array.isArray(response)) {
              return response.length; // Неточно, но это лучше чем 0
            }
            
            // Если ответ - объект с метаданными
            if (response && typeof response === 'object') {
              if (response.totalCount) return response.totalCount;
              if (response.data && Array.isArray(response.data)) return response.data.length;
            }
            
            return 0;
          };
          
          setStats({
            products: getCount(productsResponse),
            stores: getCount(storesResponse),
            priceTags: getCount(priceTagsResponse)
          });
        } catch (error) {
          console.error('Ошибка при получении статистики из отдельных запросов:', error);
          throw error;
        }
      } catch (err) {
        console.error('Ошибка при получении статистики:', err);
        setError('Не удалось загрузить статистику. Пожалуйста, попробуйте позже.');
        toast.error('Не удалось загрузить статистику');
      } finally {
        setLoading(false);
      }
    };
    
    fetchStats();
  }, []);

  return (
    <div>
      <header className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Панель управления</h1>
        <p className="text-gray-600">Добро пожаловать, {user?.fullName || 'Пользователь'}!</p>
      </header>
      
      {error && (
        <div className="mb-6 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          <p>{error}</p>
        </div>
      )}
      
      {loading ? (
        <div className="flex items-center justify-center h-40">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard 
            title="Товары" 
            value={stats.products} 
            icon="box" 
            color="blue" 
            onClick={() => window.location.href = '/products'} 
          />
          <StatCard 
            title="Магазины" 
            value={stats.stores} 
            icon="store" 
            color="green" 
            onClick={() => window.location.href = '/stores'} 
          />
          <StatCard
            title="Ценники"
            value={stats.priceTags}
            icon="tag"
            color="yellow"
            onClick={() => window.location.href = '/price-tags'}
          />
          <StatCard
            title="Пользователи"
            value={stats.users}
            icon="users"
            color="red"
            onClick={() => window.location.href = '/users'}
          />
        </div>
      )}
      
      <PriceTagChart />

      <AIStatus />
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <div className="lg:col-span-2">
          <RecentActivity />
        </div>
        <div>
          <QuickActions />
        </div>
      </div>
    
    </div>
  );
};

export default DashboardPage;